import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconChartBar,
  IconCode,
  IconDatabase,
  IconGauge,
  IconInfoCircle,
  IconSettings,
  IconTrophy,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/PageHeader";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: string;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export const LeaderboardDocumentation = () => {
  const { classes } = useStyles();

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Leaderboard Documentation"
        subtitle="Complete reference for the leaderboard ranking system"
        showBreadcrumbs={false}
      />

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <a href="#overview">Overview</a>
            </List.Item>
            <List.Item>
              <a href="#hierarchy">Data Hierarchy</a>
            </List.Item>
            <List.Item>
              <a href="#setup">Setup Guide</a>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <a href="#seasons">Seasons</a>
            </List.Item>
            <List.Item>
              <a href="#buckets">Buckets</a>
            </List.Item>
            <List.Item>
              <a href="#scoring">Scoring & Rankings</a>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <a href="#widgets">Embeddable Widgets</a>
            </List.Item>
            <List.Item>
              <a href="#graphql">GraphQL API Reference</a>
            </List.Item>
            <List.Item>
              <a href="#data-model">Data Model</a>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconGauge size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            The leaderboard system provides a flexible way to rank players based
            on scores, skill ratings, or other numeric metrics. It supports
            seasonal rankings with configurable tiers (buckets) for creating
            competitive ranking systems similar to those found in games like
            League of Legends, Valorant, or Overwatch.
          </Text>
          <Text mb="md">
            Leaderboards are composed of a hierarchy of nested entities:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Leaderboard
  +-- Seasons[] (time-bounded ranking periods)
       +-- Buckets[] (score tiers / ranking divisions)
            +-- Scores[] (individual player rankings within a bucket)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            Only one season can be active at a time per leaderboard. The active
            season is determined by the current date falling between the
            season's start and end dates. Score registration automatically
            targets the current active season.
          </Alert>
        </div>

        {/* ===================== HIERARCHY ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Hierarchy"
            id="hierarchy"
          />
          <Text mb="md">
            Understanding the relationship between leaderboard entities is key
            to configuring effective ranking systems.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 3 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="blue" variant="light">
                  Leaderboard
                </Badge>
              </Group>
              <Text size="sm">
                The top-level entity. Contains a name and description. Each
                leaderboard belongs to an organization and can have multiple
                seasons over time.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="cyan" variant="light">
                  Season
                </Badge>
              </Group>
              <Text size="sm">
                A time-bounded ranking period defined by start and end dates.
                Each season has a starting score that all players begin with.
                Contains multiple buckets to categorize players.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="green" variant="light">
                  Bucket
                </Badge>
              </Group>
              <Text size="sm">
                A score tier within a season (e.g. Bronze, Silver, Gold).
                Defined by a minimum score threshold and a score calculation
                type. Contains the actual player scores and rankings.
              </Text>
            </Card>
          </SimpleGrid>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            <strong>Example:</strong> A League of Legends-style ranking system
            would have a single Leaderboard, with a Season per competitive
            split, and Buckets for each tier (Iron, Bronze, Silver, Gold,
            Platinum, Diamond, Master, etc.) with increasing minimum score
            thresholds.
          </Alert>
        </div>

        {/* ===================== SETUP ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSettings size={18} />}
            title="Setup Guide"
            id="setup"
          />
          <Text mb="md">
            Setting up a leaderboard is a multi-step process. Follow these steps
            to get a working ranking system.
          </Text>

          <List type="ordered" spacing="sm" mb="md">
            <List.Item>
              <Text>
                <strong>Create a Leaderboard</strong> - Navigate to{" "}
                <Code>/leaderboards/new</Code>. Provide a name and description.
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <strong>Create a Season</strong> - Navigate to Seasons from the
                leaderboard dashboard. Set the season name, start date, end
                date, and starting score.
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <strong>Create Buckets</strong> - For each season, create one or
                more buckets. Each bucket requires a name, minimum score, and
                score calculation type. Order buckets by ascending minimum
                score.
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <strong>Register Scores</strong> - Use the console's "Register a
                score" button or the <Code>leaderboardRegisterPlayerScore</Code>{" "}
                GraphQL mutation to add player scores.
              </Text>
            </List.Item>
          </List>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Ensure the season dates include the current date for it to be
            recognized as the active season. If no active season exists, score
            registration will fail.
          </Alert>
        </div>

        {/* ===================== SEASONS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconChartBar size={18} />}
            title="Seasons"
            id="seasons"
          />
          <Text mb="md">
            Seasons define time-bounded ranking periods. Players compete within
            a season, and rankings reset when a new season begins.
          </Text>

          <Title order={4} mb="xs">
            Season Fields
          </Title>
          <Table striped className={classes.statusTable} mb="md">
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>name</Code>
                </td>
                <td>String!</td>
                <td>Display name for the season (e.g. "Season 1")</td>
              </tr>
              <tr>
                <td>
                  <Code>start</Code>
                </td>
                <td>DateTime!</td>
                <td>When the season begins</td>
              </tr>
              <tr>
                <td>
                  <Code>end</Code>
                </td>
                <td>DateTime!</td>
                <td>When the season ends</td>
              </tr>
              <tr>
                <td>
                  <Code>startingScore</Code>
                </td>
                <td>Float!</td>
                <td>The initial score all players start with in this season</td>
              </tr>
            </tbody>
          </Table>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            The <strong>current season</strong> is automatically determined by
            the server based on which season's date range contains the current
            date. There is no manual "activate" action.
          </Alert>
        </div>

        {/* ===================== BUCKETS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTrophy size={18} />}
            title="Buckets"
            id="buckets"
          />
          <Text mb="md">
            Buckets divide a season into score-based tiers. Players are placed
            in buckets based on their current score relative to the bucket's
            minimum score threshold.
          </Text>

          <Title order={4} mb="xs">
            Bucket Fields
          </Title>
          <Table striped className={classes.statusTable} mb="md">
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>name</Code>
                </td>
                <td>String!</td>
                <td>Display name (e.g. "Gold", "Diamond")</td>
              </tr>
              <tr>
                <td>
                  <Code>minScore</Code>
                </td>
                <td>Float!</td>
                <td>Minimum score required to be placed in this bucket</td>
              </tr>
              <tr>
                <td>
                  <Code>scoreCalculationType</Code>
                </td>
                <td>Enum</td>
                <td>
                  How scores are calculated. Currently: <Code>SCORE</Code>{" "}
                  (direct score value)
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Score Fields (per player in a bucket)
          </Title>
          <Table striped className={classes.statusTable} mb="md">
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>score</Code>
                </td>
                <td>Float!</td>
                <td>Current score</td>
              </tr>
              <tr>
                <td>
                  <Code>rank</Code>
                </td>
                <td>Float!</td>
                <td>Current ranking position within the bucket</td>
              </tr>
              <tr>
                <td>
                  <Code>lastScore</Code>
                </td>
                <td>Float</td>
                <td>Previous score before last update</td>
              </tr>
              <tr>
                <td>
                  <Code>lastRank</Code>
                </td>
                <td>Int</td>
                <td>Previous rank before last update</td>
              </tr>
              <tr>
                <td>
                  <Code>playerProfileId</Code>
                </td>
                <td>ID!</td>
                <td>The player this score belongs to</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== SCORING ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconChartBar size={18} />}
            title="Scoring & Rankings"
            id="scoring"
          />
          <Text mb="md">
            Scores are registered for a player on a specific leaderboard. The
            system automatically determines the correct active season and places
            the player in the appropriate bucket based on their score.
          </Text>

          <Title order={4} mb="xs">
            Registering a Score
          </Title>
          <Text mb="sm">
            Use the <Code>leaderboardRegisterPlayerScore</Code> mutation to
            register or update a player's score. If the player already has a
            score, it will be updated.
          </Text>
          <GqlBlock>
            {`mutation ($input: LeaderboardRegisterPlayerScoreInput!) {
  leaderboardRegisterPlayerScore(input: $input) {
    rank
    score
    lastRank
    lastScore
    playerProfileId
    leaderboardSeasonBucketId
  }
}

# Input:
# {
#   leaderboardId: ID!    - The leaderboard to register the score on
#   playerId: ID!         - The player's profile ID
#   score: PositiveInt!   - The score value (must be positive)
# }`}
          </GqlBlock>

          <Text mt="md" mb="sm" size="sm" c="dimmed">
            Requires permission:{" "}
            <Code>organization:leaderboard:register_score</Code>
          </Text>

          <Title order={4} mt="lg" mb="xs">
            Getting a Player's Current Ranking
          </Title>
          <Text mb="sm">
            Query a specific player's current ranking on a leaderboard:
          </Text>
          <GqlBlock>
            {`query ($input: LeaderboardGetPlayerRankingInput!) {
  leaderboardCurrentPlayerRanking(input: $input) {
    rank
    score
    lastRank
    lastScore
    playerProfileId
    leaderboardSeasonBucketId
  }
}

# Input:
# {
#   leaderboardId: ID!   - The leaderboard to query
#   playerId: ID!        - The player's profile ID
#   seasonId: ID         - Optional: specific season (defaults to current active season)
# }`}
          </GqlBlock>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            When a score is registered, the system automatically recalculates
            the player's rank within their bucket. The <Code>lastRank</Code> and{" "}
            <Code>lastScore</Code> fields track the previous values, allowing
            you to show rank changes (e.g. "+3 positions").
          </Alert>
        </div>

        {/* ===================== WIDGETS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="Embeddable Widgets"
            id="widgets"
          />
          <Text mb="md">
            Leaderboards can be embedded on external websites using the
            JavaScript widget. The widget fetches live data from the API and
            renders a customizable leaderboard display.
          </Text>

          <Title order={4} mb="xs">
            Widget Configuration
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>containerId</Code> - DOM element ID to render into
            </List.Item>
            <List.Item>
              <Code>leaderboardId</Code> - The leaderboard to display
            </List.Item>
            <List.Item>
              <Code>organizationId</Code> - Your organization ID
            </List.Item>
            <List.Item>
              <Code>bucketId</Code> - Optional: display a specific bucket only
            </List.Item>
            <List.Item>
              <Code>leaderboardElementFactory</Code> - Optional: custom
              rendering function for full control over the HTML output
            </List.Item>
          </List>
          <Text size="sm" c="dimmed">
            Visit the Widgets tab on any leaderboard to get a working code
            example with your leaderboard and organization IDs pre-filled.
          </Text>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete list of all GraphQL operations available for the
            leaderboard feature.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="leaderboard-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Leaderboard Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      leaderboards (list)
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float) {
  leaderboards(page: { first: $count, after: $cursor }) {
    nodes { id, name, description }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      leaderboard (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  leaderboard(id: $id) {
    id
    name
    description
    organizationId
    ownerId
    createdAt
    updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      currentLeaderboardSeason
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Returns the currently active season for a leaderboard (the
                      season whose date range includes today).
                    </Text>
                    <GqlBlock>
                      {`query ($leaderboardId: ID!) {
  currentLeaderboardSeason(leaderboardId: $leaderboardId) {
    id
    name
    start
    end
    startingScore
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="leaderboard-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Leaderboard Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      createLeaderboard
                    </Text>
                    <GqlBlock>
                      {`mutation ($name: String!, $description: String!) {
  createLeaderboard(input: { name: $name, description: $description }) {
    id
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateLeaderboard
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $name: String, $description: String) {
  updateLeaderboard(id: $id, input: { name: $name, description: $description }) {
    id
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteLeaderboard
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) { deleteLeaderboard(id: $id) }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="season-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    SEASONS
                  </Badge>
                  <Text>Season Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      leaderboardSeasons (list)
                    </Text>
                    <GqlBlock>
                      {`query ($leaderboardId: ID!, $cursor: String, $count: Float) {
  leaderboardSeasons(
    leaderboardId: $leaderboardId
    page: { first: $count, after: $cursor }
  ) {
    nodes { id, name, start, end, startingScore }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      leaderboardSeason (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  leaderboardSeason(id: $id) {
    id, name, start, end, startingScore,
    leaderboardId, createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      createLeaderboardSeason
                    </Text>
                    <GqlBlock>
                      {`mutation ($leaderboardId: ID!, $input: CreateLeaderboardSeasonInput!) {
  createLeaderboardSeason(leaderboardId: $leaderboardId, input: $input) {
    id
  }
}

# CreateLeaderboardSeasonInput:
# { name: String!, start: DateTime!, end: DateTime!, startingScore: Float! }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateLeaderboardSeason
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: UpdateLeaderboardSeasonInput!) {
  updateLeaderboardSeason(id: $id, input: $input) { id }
}

# UpdateLeaderboardSeasonInput:
# { name: String, start: DateTime, end: DateTime, startingScore: Float }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteLeaderboardSeason
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) { deleteLeaderboardSeason(id: $id) }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="bucket-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="orange" className={classes.mutationBadge}>
                    BUCKETS
                  </Badge>
                  <Text>Bucket Queries & Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      leaderboardSeasonBuckets (list)
                    </Text>
                    <GqlBlock>
                      {`query ($seasonId: ID!, $cursor: String, $count: Float) {
  leaderboardSeasonBuckets(
    seasonId: $seasonId
    page: { first: $count, after: $cursor }
  ) {
    nodes { id, name, minScore, scoreCalculationType }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      leaderboardSeasonBucket (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  leaderboardSeasonBucket(id: $id) {
    id, name, minScore, scoreCalculationType,
    seasonId, createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      leaderboardSeasonBucketScores (paginated scores)
                    </Text>
                    <GqlBlock>
                      {`query ($bucketId: ID!, $cursor: String, $count: Float) {
  leaderboardSeasonBucketScores(
    leaderboardSeasonBucketId: $bucketId
    page: { first: $count, after: $cursor }
  ) {
    nodes {
      playerProfileId, score, rank, lastScore, lastRank,
      leaderboardSeasonBucketId, createdAt, updatedAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      createLeaderboardSeasonBucket
                    </Text>
                    <GqlBlock>
                      {`mutation ($seasonId: ID!, $input: CreateLeaderboardSeasonBucketInput!) {
  createLeaderboardSeasonBucket(seasonId: $seasonId, input: $input) {
    id
  }
}

# CreateLeaderboardSeasonBucketInput:
# { name: String!, minScore: Float!, scoreCalculationType: LeaderboardScoreCalculationType! }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateLeaderboardSeasonBucket
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: UpdateLeaderboardSeasonBucketInput!) {
  updateLeaderboardSeasonBucket(id: $id, input: $input) { id }
}

# UpdateLeaderboardSeasonBucketInput:
# { name: String, minScore: Float, scoreCalculationType: LeaderboardScoreCalculationType }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteLeaderboardSeasonBucket
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) { deleteLeaderboardSeasonBucket(id: $id) }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="scoring-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="violet" className={classes.mutationBadge}>
                    SCORING
                  </Badge>
                  <Text>Score Registration & Ranking</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      leaderboardRegisterPlayerScore
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Register or update a player's score on a leaderboard.
                      Automatically targets the current active season.
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: LeaderboardRegisterPlayerScoreInput!) {
  leaderboardRegisterPlayerScore(input: $input) {
    rank
    score
    lastRank
    lastScore
    playerProfileId
    leaderboardSeasonBucketId
    createdAt
    updatedAt
  }
}

# LeaderboardRegisterPlayerScoreInput:
# { leaderboardId: ID!, playerId: ID!, score: PositiveInt! }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      leaderboardCurrentPlayerRanking
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Get the current ranking of a specific player on a
                      leaderboard. Optionally specify a season.
                    </Text>
                    <GqlBlock>
                      {`query ($input: LeaderboardGetPlayerRankingInput!) {
  leaderboardCurrentPlayerRanking(input: $input) {
    rank
    score
    lastRank
    lastScore
    playerProfileId
    leaderboardSeasonBucketId
    createdAt
    updatedAt
  }
}

# LeaderboardGetPlayerRankingInput:
# { leaderboardId: ID!, playerId: ID!, seasonId: ID }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="input-types">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="gray" className={classes.mutationBadge}>
                    INPUTS
                  </Badge>
                  <Text>Input Types Reference</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      Leaderboard Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateLeaderboardInput {
  name: String!
  description: String!
}

input UpdateLeaderboardInput {
  name: String
  description: String
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Season Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateLeaderboardSeasonInput {
  name: String!
  start: DateTime!
  end: DateTime!
  startingScore: Float!
}

input UpdateLeaderboardSeasonInput {
  name: String
  start: DateTime
  end: DateTime
  startingScore: Float
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Bucket Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateLeaderboardSeasonBucketInput {
  name: String!
  minScore: Float!
  scoreCalculationType: LeaderboardScoreCalculationType!
}

input UpdateLeaderboardSeasonBucketInput {
  name: String
  minScore: Float
  scoreCalculationType: LeaderboardScoreCalculationType
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Score & Ranking Inputs
                    </Text>
                    <GqlBlock>
                      {`input LeaderboardRegisterPlayerScoreInput {
  leaderboardId: ID!
  playerId: ID!
  score: PositiveInt!
}

input LeaderboardGetPlayerRankingInput {
  leaderboardId: ID!
  playerId: ID!
  seasonId: ID            # Optional: defaults to current active season
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Text mb="md">
            Key types and enums used throughout the leaderboard module. All
            types are generated from the GraphQL schema via codegen.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="enums">
              <Accordion.Control>Enums</Accordion.Control>
              <Accordion.Panel>
                <div>
                  <Text fw={600} size="sm" mb="xs">
                    LeaderboardScoreCalculationType
                  </Text>
                  <GqlBlock>{`SCORE    # Direct score value`}</GqlBlock>
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="entities">
              <Accordion.Control>Entity Hierarchy</Accordion.Control>
              <Accordion.Panel>
                <GqlBlock>
                  {`Leaderboard
├── id: ID!
├── name: String!
├── description: String!
├── organizationId: ID!
├── ownerId: ID!
├── createdAt: DateTime!
├── updatedAt: DateTime!
└── seasons: [LeaderboardSeason]  (via leaderboardSeasons query)
    ├── id: ID!
    ├── name: String!
    ├── start: DateTime!
    ├── end: DateTime!
    ├── startingScore: Float!
    ├── leaderboardId: ID!
    ├── createdAt: DateTime!
    ├── updatedAt: DateTime!
    └── buckets: [LeaderboardSeasonBucket]  (via leaderboardSeasonBuckets query)
        ├── id: ID!
        ├── name: String!
        ├── minScore: Float!
        ├── scoreCalculationType: LeaderboardScoreCalculationType!
        ├── seasonId: ID!
        ├── createdAt: DateTime!
        ├── updatedAt: DateTime!
        └── scores: [LeaderboardSeasonBucketScore]  (via leaderboardSeasonBucketScores query)
            ├── playerProfileId: ID!
            ├── score: Float!
            ├── rank: Float!
            ├── lastScore: Float
            ├── lastRank: Int
            ├── leaderboardSeasonBucketId: ID!
            ├── createdAt: DateTime!
            └── updatedAt: DateTime!`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== CUSTOM FIELDS & METADATA ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Custom Fields & Metadata"
            id="custom-fields-metadata"
          />
          <Text mb="md">
            Leaderboards support the platform-wide{" "}
            <Text fw={700} span>
              Custom Fields
            </Text>{" "}
            and{" "}
            <Text fw={700} span>
              Object Metadata
            </Text>{" "}
            systems from the Data service.
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <Text fw={600} span>
                Custom Fields
              </Text>{" "}
              — Structured, typed fields defined in Data &gt; Custom Fields for
              the <Code>Leaderboard</Code> object type. Supports validation,
              visibility, and editability controls.
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Object Metadata
              </Text>{" "}
              — Flexible key-value store for attaching arbitrary data to
              leaderboards. Each entry has a type (String, Boolean, URL, Image
              URL) and visibility (Public or Private).
            </List.Item>
          </List>
          <GqlBlock>
            {`# Query custom field values for a leaderboard
query ($objectType: String!, $objectId: ID!) {
  customFieldValues(objectType: $objectType, objectId: $objectId) {
    definition { key, name, type, required }
    value
  }
}
# objectType: "Leaderboard"

# Query metadata for a leaderboard
query ($objectType: String!, $objectId: ID!) {
  objectMetadata(objectType: $objectType, objectId: $objectId) {
    key, value, type, visibility
  }
}`}
          </GqlBlock>
          <Alert icon={<IconInfoCircle />} color="cyan" variant="light" mt="md">
            See the{" "}
            <Anchor href="/data/documentations">Data Documentation</Anchor> for
            the full Custom Fields & Metadata API reference, including how to
            create definitions, set values, and manage metadata entries.
          </Alert>
        </div>
      </Stack>
    </div>
  );
};
